import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { IMyDate, IMyOptions, InputsModule, InputUtilitiesModule, LocaleService, MDBBootstrapModulesPro, MDBDatePickerComponent } from 'ng-uikit-pro-standard';
import { BaryComponentHelper } from 'app/shared/util/bary-component-helper';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Transporter } from '../../../../models/transporter.model';
import dayjs, { Dayjs } from 'dayjs';
import { CommonModule } from '@angular/common';
import { MarkAsteriskDirective } from '../../../../directives/mark-asterisk.directive';
import { TranslateDirective } from '../../../../directives/translate.directive';
import { BaryInputTextComponent } from '../bary-input-text/bary-input-text.component';

@Component({
  selector: 'jhi-bary-date-picker',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, InputUtilitiesModule, InputsModule, MDBBootstrapModulesPro, MarkAsteriskDirective, TranslateDirective, BaryInputTextComponent],
  templateUrl: './bary-date-picker.component.html',
  styleUrls: ['./bary-date-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BaryDatePickerComponent implements OnInit, OnChanges {
  @ViewChild('datePicker', { static: true }) datePicker!: MDBDatePickerComponent;
  @Input() id = BaryComponentHelper.makeId();
  @Input() label = 'Date';
  @Input() placeholder = 'Date';
  @Input() icon: string | undefined;
  @Input() disabled = false;
  @Input() control: FormControl = new FormControl('');
  @Input() options!: { minDate: IMyDate; maxDate: IMyDate; disableUntilDate?: IMyDate; disablePickupDays?: IMyDate[] };
  @Input() forceRefreshOptions: EventEmitter<{ minDate: IMyDate; maxDate: IMyDate }> = new EventEmitter();
  @Input() isDisableWeekends: boolean | undefined = true;
  @Input() isPickup: boolean | undefined = false;
  @Input() limitDay?: number;
  @Input() isUpdate?: boolean | undefined;
  @Input() transporter: Transporter | undefined;
  @Input() isExportBillAndRefund: boolean | undefined = false;
  @Input() touched = false;
  @Output() dateChange = new EventEmitter<Dayjs>();

  DEFAULT_OPTIONS = {
    minDate: { year: dayjs().year() - 1, month: dayjs().month() + 1, day: dayjs().date() },
    maxDate: { year: dayjs().year(), month: dayjs().month() + 3, day: dayjs().date() },
    disableUntilDate: undefined,
    disablePickupDays: [] as IMyDate[] | undefined,
  };
  DATE_FORMAT = 'YYYY-MM-DD';
  MOMENT_DATE_FORMAT = 'dddd DD MMMM YYYY';

  _myDatePickerOptions!: IMyOptions;
  _textControl: FormControl = new FormControl('');
  _datePickerControl!: FormControl;

  public locales = {
    fr: {
      dayLabels: { su: 'Dim', mo: 'Lun', tu: 'Mar', we: 'Mer', th: 'Jeu', fr: 'Ven', sa: 'Sam' },
      dayLabelsFull: {
        su: 'Dimanche',
        mo: 'Lundi',
        tu: 'Mardi',
        we: 'Mercredi',
        th: 'Jeudi',
        fr: 'Vendredi',
        sa: 'Samedi',
      },
      monthLabels: {
        1: 'Jan',
        2: 'Fev',
        3: 'Mar',
        4: 'Avr',
        5: 'Mai',
        6: 'Juin',
        7: 'Juil',
        8: 'Aou',
        9: 'Sep',
        10: 'Oct',
        11: 'Nov',
        12: 'Dec',
      },
      monthLabelsFull: {
        1: 'Janvier',
        2: 'Février',
        3: 'Mars',
        4: 'Avril',
        5: 'Mai',
        6: 'Juin',
        7: 'Juillet',
        8: 'Août',
        9: 'Septembre',
        10: 'Octobre',
        11: 'Novembre',
        12: 'Décembre',
      },
      todayBtnTxt: "Aujourd'hui",
      clearBtnTxt: 'Effacer',
      closeBtnTxt: 'Fermer',
      firstDayOfWeek: 'mo',
      sunHighlight: true,
    },
  };

  constructor(private localeService: LocaleService) {
    this.localeService.setLocaleOptions(this.locales);
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const change in changes) {
      if (change === 'touched' && changes['touched'].currentValue) {
        this._textControl.markAsTouched();
      }
    }
  }

  ngOnInit(): void {
    this.options = { ...this.DEFAULT_OPTIONS, ...this.options };
    this.generateDataForm();

    if (this.control.hasValidator(Validators.required)) {
      this._textControl.setValidators(Validators.required);
    }

    this.forceRefreshOptions.subscribe(date => {
      this._myDatePickerOptions.disableUntil = {
        year: date.minDate.year,
        month: date.minDate.month,
        day: date.minDate.day - 1,
      };
      this.datePicker.setOptions();
    });

    this.control.valueChanges.subscribe((dateAsDayJS: Dayjs) => {
      if (dayjs.isDayjs(dateAsDayJS)) {
        const date = dateAsDayJS.format(this.DATE_FORMAT);
        if (date !== this._datePickerControl.value) {
          this._textControl.setValue(dateAsDayJS.format(this.MOMENT_DATE_FORMAT));
          this._datePickerControl.setValue(date);
        }
      }
    });

    this._datePickerControl.valueChanges.subscribe((dateAsString: string) => {
      if (dateAsString) {
        // patch value as text
        const date = dayjs(dateAsString, this.DATE_FORMAT);
        this._textControl.setValue(date.format(this.MOMENT_DATE_FORMAT));
        // patch value as date
        this.control.setValue(date);
        if (this.datePicker.isOpen) {
          this.datePicker.closeBtnClicked();
        }
      } else {
        setTimeout(() => {
          this.datePicker.updateDateValue(this.options.minDate, true);
        }, 10);
      }
    });
  }

  generateDataForm(): void {
    if (this.control.value) {
      const momentDate = dayjs(this.control.value);
      this._textControl.setValue(momentDate.format(this.MOMENT_DATE_FORMAT), { emitEvent: false });

      const myDate = this.formatDayjsToMyDate(momentDate);
      this._datePickerControl = new FormControl(this.formatDayjsToMyDate(momentDate), this.control.validator);
      this.datePicker.selectDate(myDate);
    } else {
      this._datePickerControl = new FormControl(undefined, this.control.validator);
    }

    if (!this.isUpdate && !this.isExportBillAndRefund) {
      if (this.isPickup) {
        this._myDatePickerOptions = {
          disableSince: this.options.maxDate,
          disableUntil: {
            year: this.options.minDate.year,
            month: this.options.minDate.month,
            day: this.options.minDate.day - 1,
          },
          disableDays: this.options.disablePickupDays,
          disableWeekends: this.isDisableWeekends,
          minYear: this.options.minDate.year,
          maxYear: this.options.maxDate.year,
          showTodayBtn: false,
          showClearDateBtn: false,
          indicateInvalidDate: false,
          markCurrentDay: false,
        };
      } else {
        let disableSinceAtOneMonth = this.options.maxDate;

        if (this.limitDay && !isNaN(this.limitDay)) {
          const limitDate = dayjs(this.control.value).add(this.limitDay, 'day');

          disableSinceAtOneMonth = {
            year: this.formatDayjsToMyDate(limitDate).year,
            month: this.formatDayjsToMyDate(limitDate).month,
            day: this.formatDayjsToMyDate(limitDate).day, // ne pas mettre -1 car il bloque à partir de cette date
          };
        }
        const disableUntilDate = this.options.disableUntilDate
          ? this.options.disableUntilDate
          : {
              year: this.formatDayjsToMyDate(dayjs(this.control.value)).year,
              month: this.formatDayjsToMyDate(dayjs(this.control.value)).month,
              day: this.formatDayjsToMyDate(dayjs(this.control.value)).day - 1,
            };
        this._myDatePickerOptions = {
          disableUntil: disableUntilDate,
          disableSince: disableSinceAtOneMonth,
          disableWeekends: this.isDisableWeekends,
          minYear: this.options.minDate.year,
          maxYear: this.options.maxDate.year,
          showTodayBtn: false,
          showClearDateBtn: false,
          indicateInvalidDate: false,
          markCurrentDay: false,
        };
      }
    } else {
      this._myDatePickerOptions = {
        maxYear: this.options.maxDate.year,
        showTodayBtn: false,
        showClearDateBtn: false,
      };
    }
  }

  openCalendar(): void {
    this.datePicker.openBtnClicked();
  }

  formatDayjsToMyDate(date: Dayjs): IMyDate {
    return { year: date.year(), month: date.month() + 1, day: date.date() };
  }
}
